import React, { useState, useRef, useEffect } from 'react';
import '../styles/chat-interface.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const ChatInterface = ({ onBack }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    inputRef.current?.focus();
    return () => abortControllerRef.current?.abort();
  }, []);

  const sendMessage = async () => {
    const query = input.trim();
    if (!query || isStreaming) return;

    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: query }]);
    setIsStreaming(true);

    const aiMessageId = Date.now();
    setMessages((prev) => [...prev, { role: 'ai', content: '', id: aiMessageId, streaming: true }]);

    abortControllerRef.current = new AbortController();

    try {
      const response = await fetch(
        `${API_BASE_URL}/chat?q=${encodeURIComponent(query)}`,
        { signal: abortControllerRef.current.signal }
      );

      if (!response.ok) throw new Error(`Request failed: ${response.statusText}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // keep the last (possibly incomplete) line

        for (const line of lines) {
          // Backend sends "data: <json-encoded token>\n\n"
          if (!line.startsWith('data:')) continue;
          const payload = line.slice(5).trim();
          if (!payload || payload === '[DONE]') continue;
          let token;
          try { token = JSON.parse(payload); } catch { token = payload; }
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === aiMessageId ? { ...msg, content: msg.content + token } : msg
            )
          );
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === aiMessageId
              ? { ...msg, content: msg.content || 'An error occurred. Please try again.', error: true }
              : msg
          )
        );
      }
    } finally {
      setMessages((prev) =>
        prev.map((msg) => (msg.id === aiMessageId ? { ...msg, streaming: false } : msg))
      );
      setIsStreaming(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleStop = () => {
    abortControllerRef.current?.abort();
  };

  return (
    <div className="chat-container">
      <div className="chat-header">
        <button className="chat-back-btn" onClick={onBack} type="button">
          ← Back
        </button>
        <div className="chat-header-info">
          <span className="chat-header-icon">💬</span>
          <div>
            <h2 className="chat-header-title">Free Chat</h2>
            <p className="chat-header-subtitle">Ask anything — AI responds with streaming</p>
          </div>
        </div>
      </div>

      <div className="chat-messages">
        {messages.length === 0 && (
          <div className="chat-empty-state">
            <div className="chat-empty-icon">🤖</div>
            <h3 className="chat-empty-title">Start a conversation</h3>
            <p className="chat-empty-subtitle">Type a question or message below to get started</p>
          </div>
        )}

        {messages.map((msg, idx) => (
          <div key={msg.id || idx} className={`chat-message chat-message--${msg.role}`}>
            <div className="chat-message-avatar">
              {msg.role === 'user' ? '👤' : '🤖'}
            </div>
            <div className={`chat-message-bubble ${msg.error ? 'chat-message-bubble--error' : ''}`}>
              <span className="chat-message-role">
                {msg.role === 'user' ? 'You' : 'AI'}
              </span>
              <p className="chat-message-content">
                {msg.content}
                {msg.streaming && <span className="chat-cursor" />}
              </p>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input-area">
        <textarea
          ref={inputRef}
          className="chat-input"
          placeholder="Type your message… (Enter to send, Shift+Enter for newline)"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isStreaming}
          rows={1}
        />
        {isStreaming ? (
          <button className="chat-send-btn chat-send-btn--stop" onClick={handleStop} type="button">
            ⏹ Stop
          </button>
        ) : (
          <button
            className="chat-send-btn"
            onClick={sendMessage}
            disabled={!input.trim()}
            type="button"
          >
            Send ➤
          </button>
        )}
      </div>
    </div>
  );
};

export default ChatInterface;
